import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './lib/auth.js';
import { Layout } from './modules/layout/Layout.js';
import { Login } from './modules/auth/Login.js';
import { HomeBase } from './modules/home/HomeBase.js';
import { StubPage } from './modules/home/StubPage.js';
import { Settings } from './modules/settings/Settings.js';
import { SettingsHub } from './modules/settings/SettingsHub.js';
import { PlaidIntegration } from './modules/integrations/Plaid.js';
import { GmailBills } from './modules/integrations/GmailBills.js';
import { FamilyMembersList } from './modules/family/FamilyMembersList.js';
import { FamilyMemberDetail } from './modules/family/FamilyMemberDetail.js';
import { AddFamilyMemberWizard } from './modules/family/AddFamilyMemberWizard.js';
import { DocumentsList } from './modules/documents/DocumentsList.js';
import { DocumentDetail } from './modules/documents/DocumentDetail.js';
import { TriageQueue } from './modules/documents/TriageQueue.js';
import { ProcessingMonitor } from './modules/documents/ProcessingMonitor.js';
import { DocumentLibrary } from './modules/documents/DocumentLibrary.js';
import { Reclassify } from './modules/documents/Reclassify.js';
import { EntitiesList } from './modules/entities/EntitiesList.js';
import { AiSettings } from './modules/settings/AiSettings.js';
import { Financials } from './modules/financials/Financials.js';
import { OrgChart } from './modules/org/OrgChart.js';
import { BillsList } from './modules/bills/BillsList.js';
import { BillDetail } from './modules/bills/BillDetail.js';
import { AccountsList } from './modules/accounts/AccountsList.js';
import { UsersAdmin } from './modules/admin/UsersAdmin.js';
import { InviteUser } from './modules/admin/InviteUser.js';
import { AuditLog } from './modules/admin/AuditLog.js';
import { DropboxIntegration } from './modules/integrations/Dropbox.js';
import { StorageInfo } from './modules/integrations/StorageInfo.js';
import { SecurityActions } from './modules/security/SecurityActions.js';
import { MfaEnroll } from './modules/security/MfaEnroll.js';
import { FileExplorer } from './modules/files/FileExplorer.js';
import { TradingPortal } from './modules/trading/TradingPortal.js';

export function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#FFFFFF]">
        <div className="text-stone-400">Loading…</div>
      </div>
    );
  }

  if (!user) return <Login />;

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<HomeBase />} />
        <Route path="/documents" element={<DocumentsList />} />
        <Route path="/documents/triage" element={<TriageQueue />} />
        <Route path="/documents/processing" element={<ProcessingMonitor />} />
        <Route path="/documents/library" element={<DocumentLibrary />} />
        <Route path="/documents/reclassify" element={<Reclassify />} />
        <Route
          path="/documents/export"
          element={
            <StubPage
              title="Master .xlsx export"
              blurb="One-click export of every doc's metadata, summary, and key fields as a fully-populated spreadsheet. Built in milestone M1.6."
              module="Documents · M1.6"
            />
          }
        />
        <Route path="/documents/:docId" element={<DocumentDetail />} />
        <Route path="/bills" element={<BillsList />} />
        <Route
          path="/bills/calendar"
          element={
            <StubPage
              title="Bills calendar"
              blurb="Month view of upcoming bills, plotted on their due dates. Lands in milestone M1.5e."
              module="Bill Pay · M1.5e"
            />
          }
        />
        <Route path="/bills/:billId" element={<BillDetail />} />
        <Route path="/accounts" element={<AccountsList />} />
        <Route path="/trading" element={<TradingPortal />} />
        <Route path="/entities" element={<EntitiesList />} />
        <Route path="/org" element={<OrgChart />} />
        <Route path="/financials" element={<Financials />} />
        <Route
          path="/assets"
          element={
            <StubPage
              title="Assets & liabilities"
              blurb="Cash, public equity, private fund interests, real estate, art, watches, loans — all rolled up by entity, sourced from your Module 1 docs. Lands in Module 3."
              module="Module 3"
            />
          }
        />
        <Route
          path="/assets/liabilities"
          element={
            <StubPage
              title="Liabilities"
              blurb="Mortgages, margin, lines of credit, term loans. Module 3 view."
              module="Module 3"
            />
          }
        />
        <Route
          path="/assets/valuations"
          element={
            <StubPage
              title="Valuations"
              blurb="Continuous, company-first valuation tool — replaces the spreadsheet forest. Module 4."
              module="Module 4"
            />
          }
        />
        <Route
          path="/workspace"
          element={
            <StubPage
              title="My workspace"
              blurb="Your personal scratch area — saved searches, pinned docs, custom widgets. Use 'Edit this page' on the home base to customize what you see."
              module="Personal"
            />
          }
        />
        <Route path="/settings" element={<SettingsHub />} />
        <Route path="/settings/appearance" element={<Settings />} />
        <Route path="/settings/ai" element={<AiSettings />} />
        <Route path="/integrations/plaid" element={<PlaidIntegration />} />
        <Route path="/integrations/gmail" element={<GmailBills />} />
        <Route path="/admin/family" element={<FamilyMembersList />} />
        <Route path="/admin/family/new" element={<AddFamilyMemberWizard />} />
        <Route path="/admin/family/:memberId" element={<FamilyMemberDetail />} />
        <Route
          path="/search"
          element={
            <StubPage
              title="Search"
              blurb="Semantic search over your documents using Voyage embeddings + Firestore vector search. Type in the top bar to use it now."
              module="Documents · M1.5"
            />
          }
        />
        <Route
          path="/reports"
          element={
            <StubPage
              title="Reports"
              blurb="Custom reports — entity rollups, doc-coverage gaps, audit trail exports."
              module="Future"
            />
          }
        />
        <Route path="/admin/users" element={<UsersAdmin />} />
        <Route path="/admin/invite" element={<InviteUser />} />
        <Route path="/admin/audit" element={<AuditLog />} />
        <Route path="/security" element={<SecurityActions />} />
        <Route path="/settings/mfa" element={<MfaEnroll mode="page" />} />
        <Route path="/files" element={<FileExplorer />} />
        <Route path="/files/*" element={<FileExplorer />} />
        <Route path="/integrations/dropbox" element={<DropboxIntegration />} />
        <Route path="/integrations/storage" element={<StorageInfo />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}
